/**
 * Bank Account Form Modal Validation Schema
 * Zod schema that matches BankAccountFormModal field names exactly
 *
 * All fields use camelCase for frontend consistency
 * @see api-spec.yaml - VendorBankCreate/VendorBankUpdate schemas
 */

import { z } from 'zod';

export const bankAccountFormModalSchema = z.object({
  // Basic Information
  isDefault: z.boolean(),
  accountName: z
    .string()
    .min(1, 'vendor.validation.accountNameRequired')
    .max(100, 'vendor.validation.accountNameMax'),

  // Bank Details
  countryId: z.number({ required_error: 'vendor.validation.bankLocationRequired' }),
  bankLocation: z.string().min(1, 'vendor.validation.bankLocationRequired'), // Country name for display
  bankId: z.number().optional(),
  bankCode: z.string().min(1, 'vendor.validation.bankCodeRequired'), // Bank code is required
  bankName: z.string().min(1, 'vendor.validation.bankNameRequired'), // Bank name is required
  currency: z
    .string()
    .length(3, 'vendor.validation.currencyFormat')
    .min(1, 'vendor.validation.currencyRequired'),

  // Account Information
  accountNumber: z
    .string()
    .min(1, 'vendor.validation.accountNumberRequired')
    .max(50, 'vendor.validation.accountNumberMax'),
  branchId: z.number().optional(),
  branchCode: z.string().optional().or(z.literal('')),
  branchName: z.string().optional().or(z.literal('')),
  swiftCode: z
    .string()
    .regex(/^[A-Z]{6}[A-Z0-9]{2}([A-Z0-9]{3})?$/, 'vendor.validation.swiftCodeFormat')
    .optional()
    .or(z.literal('')),
  iban: z
    .string()
    .regex(
      /^[A-Z]{2}\d{2}[A-Z0-9]{1,30}$/,
      'vendor.validation.ibanFormat'
    )
    .optional()
    .or(z.literal('')),
  notes: z.string().optional().or(z.literal('')),
  isActive: z.boolean(),
});

export type BankAccountFormModalData = z.infer<typeof bankAccountFormModalSchema>;
